import { APP_VERSION } from "../config/appVersion";

const APP_VERSION_STORAGE_KEY = "appVersion";
const PRESERVED_STORAGE_KEYS = ["language", "languageId", "loginUnique"];

const isBrowser = typeof window !== "undefined" && typeof window.localStorage !== "undefined";

function getStorage() {
    return isBrowser ? window.localStorage : null;
}

function preserveKeys(storage) {
    return PRESERVED_STORAGE_KEYS.reduce((acc, key) => {
        const value = storage.getItem(key);
        if (value !== null && value !== undefined) {
            acc[key] = value;
        }
        return acc;
    }, {});
}

function restorePreservedKeys(storage, preservedValues) {
    Object.entries(preservedValues).forEach(([key, value]) => {
        storage.setItem(key, value);
    });
}

export function syncAppVersion() {
    const storage = getStorage();
    if (!storage) {
        return;
    }

    try {
        const storedVersion = storage.getItem(APP_VERSION_STORAGE_KEY);
        if (storedVersion === APP_VERSION) {
            return;
        }

        const preservedValues = preserveKeys(storage);
        storage.clear();
        restorePreservedKeys(storage, preservedValues);
        storage.setItem(APP_VERSION_STORAGE_KEY, APP_VERSION);
    } catch (error) {
        console.error("Failed to sync app version", error);
    }
}

function isChunkLoadError(reason) {
    if (!reason) {
        return false;
    }

    const message =
        typeof reason === "string"
            ? reason
            : typeof reason.message === "string"
                ? reason.message
                : "";
    const name = typeof reason === "object" && reason !== null ? reason.name ?? "" : "";

    return (
        name === "ChunkLoadError" ||
        /Loading chunk [\w\s/-]+ failed/i.test(message) ||
        /Failed to fetch dynamically imported module/i.test(message)
    );
}

export function registerChunkLoadRecovery() {
    if (!isBrowser) {
        return;
    }

    let reloadTriggered = false;

    const triggerReload = () => {
        if (reloadTriggered) {
            return;
        }
        reloadTriggered = true;
        try {
            window.localStorage?.removeItem(APP_VERSION_STORAGE_KEY);
        } catch (error) {
            console.warn("Failed to reset cached app version before reload", error);
        }
        window.location.reload();
    };

    window.addEventListener("unhandledrejection", (event) => {
        if (isChunkLoadError(event?.reason)) {
            event?.preventDefault?.();
            triggerReload();
        }
    });

    window.addEventListener("error", (event) => {
        if (isChunkLoadError(event?.error ?? event?.message)) {
            triggerReload();
        }
    });
}

export { APP_VERSION_STORAGE_KEY };
